'use client'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum, type Site } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'
import { memo, useEffect } from 'react'

import { connectDatadogErrorForwarder } from '@features/observability/lib/datadogErrorForwarder'
import {
  PROJECT_NAME,
  PUBLIC_DATADOG_APPLICATION_ID,
  PUBLIC_DATADOG_CLIENT_TOKEN,
  PUBLIC_DATADOG_DEV_ENABLED,
  PUBLIC_DATADOG_SERVICE,
  PUBLIC_DATADOG_SITE,
  PUBLIC_RELEASE_VERSION,
  SENTRY_NODE_ENV,
} from '@lib/constants'

export const DataDog = memo(() => {
  useEffect(() => {
    if (
      !PUBLIC_DATADOG_APPLICATION_ID ||
      !PUBLIC_DATADOG_CLIENT_TOKEN ||
      !PUBLIC_DATADOG_SERVICE ||
      !PUBLIC_DATADOG_SITE
    ) {
      console.error('>>> Datadog envs are not configured <<<', {
        PUBLIC_DATADOG_APPLICATION_ID,
        PUBLIC_DATADOG_CLIENT_TOKEN,
        PUBLIC_DATADOG_SERVICE,
        PUBLIC_DATADOG_SITE,
      })

      return
    }

    if (!PUBLIC_DATADOG_DEV_ENABLED) {
      return
    }

    // Forward bus errors to RUM. Connected on every mount (the previous mount's
    // cleanup unsubscribed); the Datadog SDK buffers addError calls until init.
    const disconnectForwarder = connectDatadogErrorForwarder()

    // Prevent double init in Strict Mode or Fast Refresh.
    if (datadogRum.getInitConfiguration()) {
      return disconnectForwarder
    }

    const isProd = SENTRY_NODE_ENV === 'production'
    const isQA = SENTRY_NODE_ENV === 'qa'

    const sessionReplaySampleRate = isProd ? 10 : isQA ? 5 : 0

    // Shared identity for both SDKs. `version` is unique per deploy (version +
    // commit sha) so uploaded sourcemaps match the exact session — keep in sync
    // with scripts/upload-datadog-sourcemaps.mjs.
    const baseConfig = {
      clientToken: PUBLIC_DATADOG_CLIENT_TOKEN,
      site: PUBLIC_DATADOG_SITE as Site,
      service: `${PUBLIC_DATADOG_SERVICE}_${PROJECT_NAME}`,
      env: SENTRY_NODE_ENV,
      version: PUBLIC_RELEASE_VERSION,
      sessionSampleRate: 100,
    }

    datadogRum.init({
      ...baseConfig,
      applicationId: PUBLIC_DATADOG_APPLICATION_ID,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true, // identify React render blocking
      sessionReplaySampleRate,
      defaultPrivacyLevel: isProd ? 'mask' : 'allow',
      plugins: [reactPlugin({ router: false })],
    })

    // Browser logs, correlated with RUM sessions. Errors arrive only through the
    // throttled bus forwarder — SDK auto-forwarding stays off so log volume
    // (and cost) can't bypass the throttle.
    datadogLogs.init({ ...baseConfig, forwardErrorsToLogs: false })

    datadogRum.setGlobalContext({
      base_service: PUBLIC_DATADOG_SERVICE,
      project: PROJECT_NAME,
    })

    if (sessionReplaySampleRate > 0) {
      datadogRum.startSessionReplayRecording()
    }

    return disconnectForwarder
  }, [])

  return null
})
